import React, { useEffect, useRef, useState } from "react";
import { toPersianDigits } from "../utils/persianDigits";

// Section A: public. Footnotes used to print as a plain endnote list at the
// bottom of the content page. They're now a small sticky "پاورقی" button
// pinned to the corner of the viewport on every content page (see
// ArticleDetailPage.js, TopicDetailPage.js, IssueTopicDetailPage.js) —
// click it to unfold a compact note card without leaving your place in the
// article. Always renders, even when the page has no footnotes yet, so the
// affordance stays consistent across every content page.
function Footnotes({ footnotes }) {
  const notes = footnotes || [];
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    function handlePointerDown(event) {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`footnotes-fab${open ? " footnotes-fab--open" : ""}`}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls="footnotes-panel"
        aria-label={open ? "بستن پاورقی" : "نمایش پاورقی"}
      >
        <span className="footnotes-fab-mark" aria-hidden="true">
          ※
        </span>
        <span className="footnotes-fab-label">پاورقی</span>
        {notes.length > 0 && (
          <span className="footnotes-fab-count" aria-hidden="true">
            {toPersianDigits(notes.length)}
          </span>
        )}
      </button>

      <div
        id="footnotes-panel"
        ref={panelRef}
        className={`footnotes-panel${open ? " footnotes-panel--open" : ""}`}
        role="region"
        aria-label="پاورقی"
        aria-hidden={!open}
      >
        <div className="footnotes-panel-header">
          <span className="footnotes-panel-title">پاورقی</span>
          <button
            type="button"
            className="footnotes-panel-close"
            onClick={() => setOpen(false)}
            aria-label="بستن"
            tabIndex={open ? 0 : -1}
          >
            ×
          </button>
        </div>

        {notes.length > 0 ? (
          <ol className="footnotes-panel-list">
            {notes.map((note, index) => (
              <li key={index} style={{ "--footnote-i": index }}>
                {/* bdi isolates this run from the <li>'s ambient rtl: a
                    note like "100 Artists' Manifestos..." has weak/neutral
                    leading digits with no direction of their own, so
                    without isolation the bidi algorithm reorders them
                    against the surrounding rtl context instead of with the
                    English text that follows — the "number on one side,
                    text on the other" bug. bdi auto-detects the note's own
                    direction from its first strong character (skipping the
                    leading digits), so Persian notes still render rtl and
                    English/mixed ones render as one coherent ltr run,
                    without touching the <li> itself — the ordered-list
                    marker stays put on the right for every item either
                    way. */}
                <bdi>{note}</bdi>
              </li>
            ))}
          </ol>
        ) : (
          <p className="footnotes-panel-empty">
            برای این صفحه هنوز پاورقی‌ای ثبت نشده است.
          </p>
        )}
      </div>
    </>
  );
}

export default Footnotes;
